/**
 * WebSocket 连接 Hook
 * 管理 WebSocket 连接生命周期，提供消息发送方法
 */
import { useEffect, useCallback } from 'react';
import { wsClient } from '@/ws/client';
import type { ClientMessage } from '@fluxlab/protocols';
import { startMessageHandler, stopMessageHandler } from '@/ws/message-handler';
import { useConnectionStore } from '@/stores/connection';

let pendingConnectionCleanup: ReturnType<typeof setTimeout> | null = null;

/** 应用级 WebSocket 生命周期，只能在根组件调用一次。 */
export function useWebSocketConnection() {
  useEffect(() => {
    if (pendingConnectionCleanup) {
      clearTimeout(pendingConnectionCleanup);
      pendingConnectionCleanup = null;
    }

    wsClient.onStateChange((state) => {
      useConnectionStore.getState().setGatewayState(state);
    });

    startMessageHandler();
    wsClient.connect();

    return () => {
      pendingConnectionCleanup = setTimeout(() => {
        stopMessageHandler();
        wsClient.disconnect();
        wsClient.onStateChange(null);
        pendingConnectionCleanup = null;
      }, 0);
    };
  }, []);
}

/** 业务组件只订阅连接状态和发送消息，不管理全局连接。 */
export function useWebSocket() {
  const gatewayState = useConnectionStore((s) => s.gatewayState);

  const send = useCallback((msg: ClientMessage) => {
    wsClient.send(msg);
  }, []);

  const isConnected = gatewayState === 'connected';

  return { isConnected, connectionState: gatewayState, send, client: wsClient };
}
